import type { CatalogPricingRuleModel } from '@/shared/generated'

import { useCallback, useMemo, useState } from 'react'
import { compareDesc, format } from 'date-fns'
import { uniqBy } from 'lodash/fp'
import { ChevronDownIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { usePricingRuleCatalogQuery } from '@/entities/pricing-rule'

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
} from '@/shared/components/ui/input-group'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { cn } from '@/shared/lib/utils'

/**
 * Single flat Select of published rule variants with client-side search
 * (specs/005 — small list, client-side is fine for v1). Reports the full
 * variant so the parent can seed param defaults.
 */
export const VariantSelect = ({
	ruleId,
	ruleVersionId,
	onSelect,
}: {
	ruleId: string
	ruleVersionId: string
	onSelect: (variant: CatalogPricingRuleModel) => void
}) => {
	const { t } = useTranslation()
	const [openRule, setOpenRule] = useState(false)
	const [openRuleVersion, setOpenRuleVersion] = useState(false)
	const { data, isLoading } = usePricingRuleCatalogQuery()
	const ruleVariants = useMemo(
		() => uniqBy('id', data?.data ?? []) ?? [],
		[data]
	)
	const ruleVersionVariants = useMemo(
		() =>
			data?.data
				?.filter((v) => v.id === ruleId)
				.filter((v) => v.version)
				.sort((a, b) =>
					compareDesc(new Date(a.version!), new Date(b.version!))
				) ?? [],
		[data, ruleId]
	)
	const selectedRule = useMemo(
		() => ruleVariants.find((v) => v.id === ruleId),
		[ruleVariants, ruleId]
	)
	const selectedRuleVersion = useMemo(
		() => ruleVersionVariants.find((v) => v.version_id === ruleVersionId),
		[ruleVersionVariants, ruleVersionId]
	)
	const isLegacyRule = !ruleVersionId
	const isLatestVersion = useCallback(
		(versionId: string) =>
			ruleVersionVariants[0] && ruleVersionVariants[0].version_id === versionId,
		[ruleVersionVariants]
	)

	return (
		<InputGroup>
			<InputGroupAddon
				align='inline-start'
				className='w-full justify-start truncate'
			>
				<Popover open={openRule} onOpenChange={setOpenRule}>
					<PopoverTrigger
						aria-expanded={openRule}
						disabled={isLoading}
						className='flex w-full cursor-pointer justify-start truncate'
						data-testid='pr-tactic-variant-select'
					>
						<div className='inline-flex truncate px-2'>
							<span
								className={cn(
									'truncate',
									!selectedRule ? 'text-muted-foreground' : 'text-foreground'
								)}
							>
								{selectedRule
									? t(selectedRule.name, {
											defaultValue: selectedRule.name,
										})
									: t('pr_tactic_select_rule', {
											defaultValue: 'Select a pricing rule',
										})}
							</span>
						</div>
					</PopoverTrigger>
					<PopoverContent
						className='w-(--radix-popover-trigger-width) p-0'
						align='start'
					>
						<Command>
							<CommandInput
								placeholder={t('general_search', {
									defaultValue: 'Search',
								})}
							/>
							<CommandList>
								<CommandEmpty>
									{t('pr_tactic_no_rules', {
										defaultValue: 'No pricing rules found.',
									})}
								</CommandEmpty>
								<CommandGroup>
									{ruleVariants.map((variant) => (
										<CommandItem
											key={variant.id}
											// Search must match what the user reads, not the raw key.
											value={t(variant.name, { defaultValue: variant.name })}
											data-value={variant.id}
											data-testid={`pr-tactic-variant-option-${variant.id}`}
											onSelect={() => {
												onSelect(variant)
												setOpenRule(false)
												setOpenRuleVersion(false)
											}}
										>
											{t(variant.name, {
												defaultValue: variant.name,
											})}
										</CommandItem>
									))}
								</CommandGroup>
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>
			</InputGroupAddon>
			{!isLegacyRule && (
				<InputGroupAddon align='inline-end'>
					<Popover open={openRuleVersion} onOpenChange={setOpenRuleVersion}>
						<PopoverTrigger asChild>
							<InputGroupButton
								variant={isLatestVersion(ruleVersionId) ? 'primary' : 'warning'}
								className='pr-1.5! text-xs'
							>
								{selectedRuleVersion
									? isLatestVersion(ruleVersionId)
										? t('latest', {
												defaultValue: 'Latest',
											})
										: '(' +
											t('outdated', {
												defaultValue: 'Outdated',
											}) +
											') ' +
											getRuleFormattedName(selectedRuleVersion)
									: t('pr_tactic_select_rule_version', {
											defaultValue: 'Select a rule version',
										})}{' '}
								<ChevronDownIcon className='size-3' />
							</InputGroupButton>
						</PopoverTrigger>
						<PopoverContent className='w-56 p-0' align='end'>
							<Command>
								<CommandInput
									placeholder={t('general_search', {
										defaultValue: 'Search',
									})}
								/>

								<CommandEmpty>
									{t('pr_tactic_no_rule_versions', {
										defaultValue: 'No rule versions found.',
									})}
								</CommandEmpty>
								<CommandGroup>
									{ruleVersionVariants.map((variant) => (
										<CommandItem
											key={variant.version_id}
											value={variant.version_id || 'V1'}
											data-value={variant.version_id}
											data-testid={`pr-tactic-variant-option-${variant.version_id}`}
											onSelect={() => {
												onSelect(variant)
												setOpenRule(false)
												setOpenRuleVersion(false)
											}}
										>
											{getRuleFormattedName(variant)}
											{isLatestVersion(variant.version_id!) && (
												<span className='ml-auto text-xs text-muted-foreground'>
													{t('latest', {
														defaultValue: 'Latest',
													})}
												</span>
											)}
										</CommandItem>
									))}
								</CommandGroup>
							</Command>
						</PopoverContent>
					</Popover>
				</InputGroupAddon>
			)}
		</InputGroup>
	)
}

const getRuleFormattedName = (rule: CatalogPricingRuleModel) => {
	if (rule.marked_name) {
		return rule.marked_name
	}
	if (rule.version) {
		return format(new Date(rule.version + 'Z'), 'MM/dd/yyyy HH:mm')
	}
}
